/**
 * "Log in with a different email address" — URL construction for the two screens that offer it.
 *
 * A switch is `prompt=select_account`: the auth worker re-authenticates over a surviving tenant SSO
 * session and signs the previous user out only once someone else logs in, so abandoning it leaves
 * the user signed in as who they were and the flow resumes rather than restarts.
 *
 * Pure on purpose: no `window`, no store, no i18n. `null` means "no URL could be built" — the
 * caller does nothing.
 */

export interface ConsentSwitchParams {
  /** The application's own login entry point, forwarded by the consent interceptor. */
  initiateLoginUri: string | null;
}

// Restart the interrupted flow at the application's own entry point, forcing re-authentication.
export function buildConsentSwitchUrl({ initiateLoginUri }: ConsentSwitchParams): string | null {
  if (!initiateLoginUri) return null;
  const url = new URL(initiateLoginUri);
  url.searchParams.set('prompt', 'select_account');
  return url.toString();
}

export interface InviteSwitchParams {
  /**
   * Opt in to `prompt=select_account`. `/invite` sets it; the `/invite-error` "create account" button
   * does not: `/oauth2/register` under a live SSO session ignores the re-auth intent (the SSO
   * user just completes registration as themselves), so a fresh account there still needs a
   * destructive logout to break the session first.
   */
  reauthenticate: boolean;
  invitationRoot: string;
  inviteId: string | null;
  language: string | null;
  screen?: string;
  brand: string | null;
}

export function buildInviteSwitchUrl({
  reauthenticate,
  invitationRoot,
  inviteId,
  language,
  screen,
  brand,
}: InviteSwitchParams): string | null {
  if (!inviteId || !invitationRoot) return null;

  const returnTo = new URL(`${invitationRoot}/invites/${inviteId}`);
  if (language) returnTo.searchParams.set('ui_locales', language);
  if (screen) returnTo.searchParams.set('screen', screen);
  if (brand) returnTo.searchParams.set('brand', brand);

  const url = new URL(`${invitationRoot}${reauthenticate ? '/auth/login' : '/auth/logout'}`);
  if (language) url.searchParams.set('ui_locales', language);
  if (reauthenticate) url.searchParams.set('prompt', 'select_account');
  url.searchParams.set('returnTo', returnTo.toString());
  return url.toString();
}
